import Button from "components/Button";
import Input from "components/Input";
import { useManualOccurrence } from "contexts/register/ManualOccurrence";
import { useEffect, useState, useReducer } from "react";
import OccurrenceManagementService from "services/OccurrenceManagementService";
import { fireToast } from "services/sweetAlertConfig";
import NoPolicemanError from "../NoPolicemanError";
import * as S from "./styles";
import { MoneyInput } from "components/MoneyInput";
import { KeyValue } from "components/display/KeyValue";

import { ReactComponent as ClockIcon } from "assets/alarm-clock.svg";
import { ReactComponent as SpeedIcon } from "assets/speed-one.svg";
import { ReactComponent as TicketIcon } from "assets/ticket-one.svg";
import { ReactComponent as CarIcon } from "assets/car.svg";
import { ReactComponent as ArrowUpIcon } from "assets/up-two.svg";
import { ReactComponent as ArrowDownIcon } from "assets/down-two.svg";
import {
  initialPaymentValues,
  sumClientValues,
  transformPaymentValues,
} from "./data";
import { AutoComplete } from "components/Autocomplete";

import { ProductRegion } from "contexts/register/ClientContext";
import { paymentValueReducer } from "./reducers";
import { CustomDivAlert } from "./styles";
import NoSelectedReportError from "../NoSelectedReportError";

export default function PaymentTab() {
  const { occurrenceToEdit, occurrenceType, refetch, selectedReport } = useManualOccurrence();

  const [productRegion, setProductRegion] = useState<ProductRegion>();
  const [mileageKmValue, setMileageKmValue] = useState<number>(0);

  const policeman = selectedReport?.superficialPoliceman || selectedReport?.policeman;

  const isRegisteredPoliceman = policeman && "user" in policeman;

  const tenant = occurrenceToEdit?.tenant;

  const tenantAdditionalHour =
    tenant?.config.maximumHoursBeforeAdditionaHourValues;
  const tenantAdditionalKM =
    tenant?.config.maximumKilometerBeforeAdditionaKmValues;

  const clientRegions = occurrenceToEdit.client.product?.productRegions ?? [];

  const getPolicemanValues = () => {
    if (policeman && "user" in policeman) {
      return occurrenceType === "patrimonial"
        ? policeman.patrimonialValues
        : policeman.vehicleValues;
    }

    return undefined;
  };

  const [paymentValues, dispatchPayment] = useReducer(
    paymentValueReducer,
    initialPaymentValues
  );

  function handleRegionChange(product: ProductRegion) {
    if (productRegion && product.id === productRegion.id) return;

    setProductRegion(product);

    const _values = { ...paymentValues };

    _values.client.hourValue.baseValue = product.hourValue;
    if (_values.client.hourValue.additional !== 0) {
      _values.client.hourValue.total =
        product.hourValue * _values.client.hourValue.additional;
    }

    _values.client.kmValue.baseValue = product.kmValue;
    if (_values.client.kmValue.additional !== 0) {
      _values.client.kmValue.total =
        product.kmValue * _values.client.kmValue.additional;
    }

    _values.client.regionValue = product.regionValue;
    _values.client.minimumBilling = occurrenceToEdit?.client.minimumBilling;

    _values.client.rec = sumClientValues(_values.client);

    dispatchPayment({
      type: "REWRITE_STATE",
      payload: 0,
      initPayload: _values,
    });
  }

  async function handleSubmit() {
    if (!selectedReport) {
      fireToast("Nenhum pronta-resposta associado a essa ocorrencia!");
      return;
    }

    if (!productRegion || !productRegion.id) {
      fireToast("Nenhuma regiao selecionada!");
      return;
    }

    try {
      await OccurrenceManagementService.updateReportPayment(
        selectedReport.id,
        {
          paymentValues,
          productRegionId: productRegion.id,
        }
      );

      fireToast("Valores de ocorrencia atualizados com sucesso!");
      refetch();
    } catch {
      fireToast("Ocorreu um erro ao atualizar os valores da ocorrencia!");
    }
  }

  const findProductRegionById = (productRegionId?: string) => {
    if (!productRegionId) return;
    for (const productRegion of clientRegions) {
      if (productRegion.id === productRegionId) return productRegion;
    }
    return;
  };

  useEffect(() => {
    if (selectedReport) {
      const report = selectedReport;
      const policemanValues = getPolicemanValues();

      const transformedPaymentValues = transformPaymentValues({
        report,
        policemanValues,
      });

      setProductRegion(
        findProductRegionById(transformedPaymentValues.client.productRegionId)
      );

      if (transformedPaymentValues !== undefined) {
        dispatchPayment({
          type: "REWRITE_STATE",
          payload: 0,
          initPayload: transformedPaymentValues,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occurrenceToEdit, selectedReport]);

  // useEffect to add the mileage km value of the policemen
  useEffect(() => {
    const policemanValues = getPolicemanValues();

    setMileageKmValue(policemanValues?.mileageKm ?? 0);
  }, []); // eslint-disable-line

  return (
    <S.Wrapper>
      {!selectedReport && (
        <NoSelectedReportError />
      )}

      {selectedReport && (
        <>
          <S.SummarySection>
            <div className="payment-header">
              <h3>Resumo de definições de valores</h3>
              <p>
                Esses são os valores registrados do cliente, do pronta-resposta e
                das suas definições de pagamento (registradas na tela de
                configurações)
              </p>
            </div>

            <S.PaymentContainer>
              <KeyValue
                label="Tipo de ocorrência"
                value={
                  occurrenceType === "patrimonial" ? "Patrimonial" : "Veicular"
                }
              />

              <KeyValue
                label="Sua hora adicional (a partir de)"
                value={`${tenantAdditionalHour} hora(s)`}
              />

              <KeyValue
                label="Sua KM adicional (a partir de)"
                value={`${tenantAdditionalKM}km`}
              />
            </S.PaymentContainer>
          </S.SummarySection>

          <S.PaymentSection data-cy="occurrence-management-payment-tab-client-values">
            <div className="payment-header">
              <h3>Valores do cliente (REC)</h3>
            </div>

            <S.FormRow>
              <AutoComplete
                id="region"
                label="Selecione a região (do cliente) de cobrança dessa ocorrência"
                placeholder="Selecione a região"
                options={clientRegions || []}
                getOptionLabel={(region) => region.name}
                onChange={(selected) => handleRegionChange(selected as ProductRegion)}
                defaultValue={productRegion}
              />
            </S.FormRow>

            <S.FormGroup isDeactivated={!productRegion}>
              <S.PaymentContainer>
                <Input
                  id="additional_km"
                  dataCy="client_additional_km_input"
                  name="additional_km"
                  type="number"
                  label="KM adicional"
                  value={String(paymentValues.client?.kmValue.additional)}
                  onChange={(event) => {
                    const eventValue = Number(event.target.value);
                    dispatchPayment({
                      type: "REC_PAYMENT_VALUE",
                      payload: eventValue,
                      valueKey: "additional",
                      valueType: "kmValue",
                    });
                  }}
                  required
                />
                <MoneyInput
                  id="client_additional_km_value"
                  labelIcon={<SpeedIcon />}
                  label="Valor da KM adicional"
                  value={paymentValues.client?.kmValue.baseValue}
                  readOnly
                />
                <MoneyInput
                  id="client_final_km_value"
                  labelIcon={<SpeedIcon />}
                  label="Valor FINAL"
                  value={paymentValues.client?.kmValue.total}
                  readOnly
                />
              </S.PaymentContainer>

              <S.PaymentContainer>
                <Input
                  id="additional_hour"
                  dataCy="client_additional_hour_input"
                  name="additional_hour"
                  type="number"
                  label="Hora adicional"
                  required
                  value={String(paymentValues.client?.hourValue.additional)}
                  onChange={(event) => {
                    const eventValue = Number(event.target.value);
                    dispatchPayment({
                      type: "REC_PAYMENT_VALUE",
                      payload: eventValue,
                      valueKey: "additional",
                      valueType: "hourValue",
                    });
                  }}
                />
                <MoneyInput
                  id="client_additional_hour_value"
                  labelIcon={<ClockIcon />}
                  label="Valor da hora adicional"
                  value={paymentValues.client.hourValue.baseValue}
                  readOnly
                />
                <MoneyInput
                  id="client_final_hour_value"
                  labelIcon={<ClockIcon />}
                  label="Valor FINAL"
                  readOnly
                  value={paymentValues.client.hourValue.total}
                />
              </S.PaymentContainer>

              <S.PaymentContainer>
                <MoneyInput
                  id="region_value"
                  labelIcon={<ArrowDownIcon />}
                  label="Valor da região"
                  readOnly
                  value={paymentValues.client.regionValue}
                />
                <MoneyInput
                  id="minimum_billing_value"
                  labelIcon={<ArrowDownIcon />}
                  label="Faturamento minimo do cliente"
                  readOnly
                  value={paymentValues.client.minimumBilling}
                />
                <MoneyInput
                  id="rec_value"
                  labelIcon={<ArrowDownIcon />}
                  label="Valor total de recebimento (REC)"
                  readOnly
                  inputContainerClassname="active-input"
                  value={paymentValues.client.rec}
                />
              </S.PaymentContainer>

              <S.DeactivatedFormGroupTitle>
                Você precisa escolher uma região de cobrança antes de inserir os
                valores de recebimento!
              </S.DeactivatedFormGroupTitle>
            </S.FormGroup>

            <footer>
              <Button
                disabled={!productRegion}
                title="Salvar valores referentes ao recebimento do cliente"
                type="button"
              />
            </footer>
          </S.PaymentSection>

          <S.PaymentSection data-cy="occurrence-management-payment-tab-policeman-values">
            <div className="payment-header">
              <h3>Valores do pronta-resposta (PR)</h3>

              {policeman && "user" in policeman && (
                <CustomDivAlert
                  text="Os valores de KM adicional, hora adicional e saída do agente 
                  foram coletadas automaticamente a partir do cadastro do pronta-resposta!"
                />
              )}

              {policeman && !("user" in policeman) && (
                <CustomDivAlert
                  text="Essa ocorrência foi feita por um pronta-resposta que não está
                  registrado no sistema. Portanto, os valores de KM adicional, hora 
                  adicional e saída do agente devem ser inseridos manualmente!"
                />
              )}
            </div>

            <S.FormGroup isDeactivated={false}>
              {!isRegisteredPoliceman && (
                <S.SuperficialPolicemanContainer>
                  <h4>Dados do pronta-resposta avulso</h4>

                  <S.PaymentFormContainer>
                    <KeyValue
                      label="Pronta-resposta"
                      value={
                        policeman && !("user" in policeman) ? policeman.name : "--"
                      }
                    />
                    <KeyValue
                      label="Contato"
                      value={
                        policeman && !("user" in policeman)
                          ? policeman.phoneNumber
                          : "--"
                      }
                    />
                  </S.PaymentFormContainer>
                </S.SuperficialPolicemanContainer>
              )}

              <S.PaymentContainer>
                <MoneyInput
                  id="mileage_km_value"
                  labelIcon={<SpeedIcon />}
                  label="Valor de franquia por KM"
                  value={mileageKmValue}
                  readOnly={isRegisteredPoliceman}
                />
              </S.PaymentContainer>

              <S.PaymentContainer>
                <Input
                  id="pr_additional_km"
                  name="pr_additional_km"
                  type="number"
                  dataCy="pr_additional_km_input"
                  label="KM adicional"
                  value={String(paymentValues.policeman.kmValue.additional)}
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    dispatchPayment({
                      type: "PR_PAYMENT_VALUE",
                      payload: value,
                      valueKey: "additional",
                      valueType: "kmValue",
                    });
                  }}
                  required
                />
                <MoneyInput
                  labelIcon={<SpeedIcon />}
                  id="additional_km_value"
                  label="Valor da KM adicional"
                  value={paymentValues.policeman.kmValue.baseValue}
                  readOnly={isRegisteredPoliceman}
                  onCurrencyChange={(currency) => {
                    dispatchPayment({
                      type: "PR_PAYMENT_VALUE",
                      payload: currency,
                      valueKey: "baseValue",
                      valueType: "kmValue",
                    });
                  }}
                />

                <MoneyInput
                  labelIcon={<SpeedIcon />}
                  id="pr_final_km_value"
                  label="Valor FINAL"
                  value={paymentValues.policeman.kmValue.total}
                  readOnly
                />
              </S.PaymentContainer>

              <S.PaymentContainer>
                <Input
                  id="pr_additional_hour"
                  name="pr_additional_hour"
                  type="number"
                  label="Hora adicional"
                  dataCy="pr_additional_hour_input"
                  required
                  value={String(paymentValues.policeman.hourValue.additional)}
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    dispatchPayment({
                      type: "PR_PAYMENT_VALUE",
                      payload: value,
                      valueKey: "additional",
                      valueType: "hourValue",
                    });
                  }}
                />
                <MoneyInput
                  id="additional_hour_value"
                  labelIcon={<ClockIcon />}
                  label="Valor da hora adicional"
                  readOnly={isRegisteredPoliceman}
                  value={paymentValues.policeman.hourValue.baseValue}
                  onCurrencyChange={(currency) => {
                    dispatchPayment({
                      type: "PR_PAYMENT_VALUE",
                      payload: currency,
                      valueKey: "baseValue",
                      valueType: "hourValue",
                    });
                  }}
                />
                <MoneyInput
                  id="pr_final_hour_value"
                  labelIcon={<ClockIcon />}
                  label="Valor FINAL"
                  readOnly
                  value={paymentValues.policeman.hourValue.total}
                />
              </S.PaymentContainer>

              <S.PaymentContainer>
                <MoneyInput
                  id="toll_value"
                  labelIcon={<TicketIcon />}
                  label="Valor de pedágio (opcional)"
                  value={paymentValues.policeman.tollValue}
                  onCurrencyChange={(currency) => {
                    dispatchPayment({
                      type: "PR_TOLL_VALUE",
                      payload: currency,
                    });
                  }}
                />
                <MoneyInput
                  id="policeman_exit_value"
                  labelIcon={<CarIcon />}
                  label="Valor de saída do agente"
                  readOnly={isRegisteredPoliceman}
                  value={paymentValues.policeman.exitValue}
                  onCurrencyChange={(currency) => {
                    dispatchPayment({
                      type: "PR_EXIT_VALUE",
                      payload: currency,
                    });
                  }}
                />
                <MoneyInput
                  id="total_policeman_payment_value"
                  labelIcon={<ArrowUpIcon />}
                  label="Valor total de pagamento ao pronta-resposta (PR)"
                  value={paymentValues.policeman.pr}
                  readOnly
                  inputContainerClassname="active-input"
                />
              </S.PaymentContainer>
            </S.FormGroup>

            <footer>
              <Button
                title="Salvar valores referentes ao pagamento do pronta-resposta"
                type="button"
                onClick={handleSubmit}
              />
            </footer>
          </S.PaymentSection>
        </>
      )}
    </S.Wrapper>
  );
}
